import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, LayoutDashboard, ClipboardList, CalendarDays, UserRound, Bell, ListChecks, BookOpen, FolderArchive } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { useFeatureFlags } from "@/lib/featureFlags";

const TABS = [
  { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "record", label: "Points", icon: ClipboardList },
  { to: "schedule", label: "Daily Schedule", icon: CalendarDays },
  { to: "choices", label: "Choices", icon: ListChecks },
  { to: "rally-books", label: "Rally Books", icon: BookOpen },
  { to: "document-depot", label: "Document Depot", icon: FolderArchive },
  { to: "profile", label: "Profile", icon: UserRound },
  { to: "notifications", label: "Notifications", icon: Bell },
];

export default function ParentStudentLayout() {
  const { choices: choicesOn, schedule: scheduleOn, rally_books: rallyBooksOn, document_depot: docDepotOn, merit_tracking: meritOn, lettering: letteringOn } = useFeatureFlags();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [info, setInfo] = useState(null);
  const [students, setStudents] = useState([]);
  const tabs = TABS.filter((t) => (t.to !== "record" || meritOn || letteringOn) && (t.to !== "choices" || choicesOn) && (t.to !== "schedule" || scheduleOn) && (t.to !== "rally-books" || rallyBooksOn) && (t.to !== "document-depot" || docDepotOn));

  useEffect(() => {
    api.get(`/parent/students/${id}/profile`)
      .then(({ data }) => setInfo(data))
      .catch(() => navigate("/parent/dashboard"));
  }, [id, navigate]);

  useEffect(() => {
    api.get("/parent/students").then(({ data }) => setStudents(data.items || [])).catch(() => {});
  }, []);

  const currentTab = location.pathname.split(`/parent/students/${id}/`)[1] || "dashboard";
  const switchStudent = (newId) => {
    if (newId && newId !== id) navigate(`/parent/students/${newId}/${currentTab}`);
  };

  return (
    <div data-testid="parent-student-layout">
      <button onClick={() => navigate("/parent/dashboard")} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800" data-testid="parent-back-to-students">
        <ArrowLeft className="h-4 w-4" /> Back to My Students
      </button>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="font-heading text-2xl font-extrabold text-slate-900" data-testid="parent-student-name">
          {info ? `${info.first_name} ${info.last_name}` : "…"}
        </h1>
        <span className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-700">View only</span>
        {info?.team_name && <span className="text-sm text-slate-500">{info.team_name}</span>}
        {students.length > 1 && (
          <Select value={id} onValueChange={switchStudent}>
            <SelectTrigger className="ml-auto w-56 rounded-full" data-testid="parent-student-switcher">
              <SelectValue placeholder="Switch student" />
            </SelectTrigger>
            <SelectContent>
              {students.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`parent-student-option-${s.id}`}>
                  {s.first_name} {s.last_name}{s.team_name ? ` · ${s.team_name}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200" data-testid="parent-student-tabs">
        {tabs.map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} data-testid={`parent-tab-${to}`}
            className={({ isActive }) =>
              `flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${isActive ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </div>

      <Outlet context={{ studentId: id, info }} />
    </div>
  );
}
